/**
 * Entra ID (Azure AD) authentication for the frontend.
 *
 * Replaces Supabase Auth. We obtain an access token for OUR backend API and send it as
 * a Bearer header (see integrations/api/client.ts). Two ways to get that token:
 *
 *   1. Inside Microsoft Teams: Teams SSO (`microsoftTeams.authentication.getAuthToken`)
 *      — silent, no popup, uses the signed-in Teams identity.
 *   2. In a plain browser: MSAL (popup / silent refresh).
 *
 * Required env vars (see .env.example):
 *   APP_ENTRA_CLIENT_ID   the SPA app registration's client id
 *   APP_ENTRA_TENANT_ID   the directory (tenant) id
 *   APP_ENTRA_API_SCOPE   the backend API scope, e.g. api://<api-client-id>/access_as_user
 */
import {
  PublicClientApplication,
  type AccountInfo,
  type Configuration,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import * as microsoftTeams from '@microsoft/teams-js';

const clientId = process.env.APP_ENTRA_CLIENT_ID as string | undefined;
const tenantId = process.env.APP_ENTRA_TENANT_ID as string | undefined;
const apiScope = process.env.APP_ENTRA_API_SCOPE as string | undefined;

/**
 * Local dev-auth: when APP_DEV_AUTH=true we skip MSAL/Teams entirely and hand out a
 * dummy bearer token. The backend's dev-auth chain (DevAuthConfig, enabled by the `dev`
 * Spring profile) ignores the token's value and treats every request as a fixed local
 * admin — so the app "auto signs in". NEVER ship a build with this enabled.
 */
const devAuth = process.env.APP_DEV_AUTH === 'true';
const DEV_TOKEN = 'dev-auth';

const msalConfig: Configuration = {
  auth: {
    clientId: clientId ?? '',
    authority: `https://login.microsoftonline.com/${tenantId ?? 'common'}`,
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'localStorage',
  },
};

const msal = new PublicClientApplication(msalConfig);
let msalReady: Promise<void> | null = null;
function ensureMsal(): Promise<void> {
  if (!msalReady) {
    msalReady = msal.initialize();
  }
  return msalReady;
}

const scopes = apiScope ? [apiScope] : [];

// --- Teams detection ---------------------------------------------------------

let teamsReady: Promise<boolean> | null = null;
function ensureTeams(): Promise<boolean> {
  if (!teamsReady) {
    teamsReady = (async () => {
      try {
        const init = microsoftTeams.app.initialize();
        // Generous timeout: a cold-starting Teams mobile webview on a slow connection can take
        // several seconds to answer this handshake. Too short a timeout here makes us wrongly
        // conclude we're NOT in Teams, which then falls back to the browser MSAL popup login —
        // showing a real username/password prompt every single time instead of silent Teams SSO.
        await Promise.race([
          init,
          new Promise((_, rej) => setTimeout(() => rej(new Error('teams-init-timeout')), 8000)),
        ]);
        await microsoftTeams.app.getContext();
        return true;
      } catch {
        return false;
      }
    })();
  }
  return teamsReady;
}



// --- Public API --------------------------------------------------------------

/** True when we appear to be running as a Teams tab. */
export async function isInTeams(): Promise<boolean> {
  if (devAuth) return false;
  return ensureTeams();
}

/** Cached signed-in account (browser/MSAL flow), if any. */
export function getAccount(): AccountInfo | null {
  return msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null;
}

/**
 * Get a bearer token for the backend API. Tries Teams SSO first, then MSAL silent,
 * then an interactive MSAL popup. Throws if the user cannot be authenticated.
 */
export async function acquireToken(): Promise<string> {
  if (devAuth) return DEV_TOKEN;
  if (await ensureTeams()) {
    // In Teams, SSO is the only viable flow — an MSAL popup is blocked inside the Teams
    // iframe. Surface the real getAuthToken error instead of silently falling back to a
    // popup (which only produces a confusing popup_window_error).
    try {
      return await microsoftTeams.authentication.getAuthToken();
    } catch (err) {
      console.error('Teams SSO getAuthToken failed:', err);
      throw new Error('Teams SSO failed: ' + (err instanceof Error ? err.message : String(err)));
    }
  }

  await ensureMsal();
  const account = getAccount();
  if (account) {
    try {
      const result = await msal.acquireTokenSilent({ scopes, account });
      return result.accessToken;
    } catch (err) {
      if (!(err instanceof InteractionRequiredAuthError)) {
        // network/other — fall through to interactive
      }
    }
  }
  const result = await msal.acquireTokenPopup({ scopes });
  if (result.account) {
    msal.setActiveAccount(result.account);
  }
  return result.accessToken;
}

/**
 * Try to get a token WITHOUT any interactive popup — Teams SSO, or an MSAL silent
 * refresh from a cached account. Returns null if the user would need to sign in
 * interactively. Use this on page load to detect an existing session.
 */
export async function acquireTokenSilent(): Promise<string | null> {
  if (devAuth) return DEV_TOKEN;
  if (await ensureTeams()) {
    try {
      return await microsoftTeams.authentication.getAuthToken();
    } catch (err) {
      console.warn('Teams SSO getAuthToken (silent) failed:', err);
      return null;
    }
  }
  await ensureMsal();
  const account = getAccount();
  if (!account) return null;
  try {
    const result = await msal.acquireTokenSilent({ scopes, account });
    return result.accessToken;
  } catch {
    return null;
  }
}

/** Interactive sign-in (browser). In Teams the token is silent, so this is a no-op there. */
export async function signIn(): Promise<void> {
  if (devAuth) return;
  if (await ensureTeams()) {
    // Teams SSO is silent; getAuthToken on the next API call establishes the session.
    await acquireToken();
    return;
  }
  await ensureMsal();
  const result = await msal.loginPopup({ scopes });
  if (result.account) {
    msal.setActiveAccount(result.account);
  }
}

export async function signOut(): Promise<void> {
  if (devAuth) return;
  if (await ensureTeams()) {
    // Nothing to clear for Teams SSO; the Teams host owns the session.
    return;
  }
  await ensureMsal();
  const account = getAccount();
  // Pass the specific account so MSAL clears THAT account's cache, and set the redirect
  // URIs so the logout popup completes and closes (returns to the app) instead of leaving
  // the user stranded on Microsoft's "pick an account" page — without these the promise
  // never resolves and the app never clears its session.
  await msal.logoutPopup({
    account: account ?? undefined,
    postLogoutRedirectUri: window.location.origin,
    mainWindowRedirectUri: window.location.origin,
  });
  msal.setActiveAccount(null);
}
